import { NOTE_TRIGGER_ATTRIBUTE } from '@/components/matrix/note-marker';
import { cn } from '@/lib/utils';
import { type ReactNode, type Ref, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Width of the bubble, and the gap kept between it, the anchor and the window's edges. */
export const NOTES_BUBBLE_WIDTH_PX = 320;
const GAP_PX = 8;

export interface BubblePlacement {
  left: number;
  top: number;
  side: 'right' | 'left';
}

/**
 * Where the bubble goes: to the right of the anchor, to its left when the
 * right side lacks the room, then kept inside the window. Pure, so it is
 * tested without a layout engine.
 */
export function placeBubble(
  anchor: { left: number; right: number; top: number },
  bubble: { width: number; height: number },
  viewport: { width: number; height: number },
): BubblePlacement {
  const fitsRight = anchor.right + GAP_PX + bubble.width + GAP_PX <= viewport.width;
  const side = fitsRight ? 'right' : 'left';
  const rawLeft = fitsRight ? anchor.right + GAP_PX : anchor.left - GAP_PX - bubble.width;
  const left = Math.max(GAP_PX, Math.min(rawLeft, viewport.width - bubble.width - GAP_PX));
  const top = Math.max(GAP_PX, Math.min(anchor.top, viewport.height - bubble.height - GAP_PX));
  return { left, top, side };
}

export interface NotesBubbleProps {
  /** The cell or header the bubble is anchored to. */
  anchor: HTMLElement;
  /** Accessible name of the bubble: the spot it is about. */
  label: string;
  /** Escape, a click elsewhere, or the anchor leaving the page. */
  onClose: () => void;
  /** Receives the bubble's element, so the page can count a click inside it as « inside ». */
  containerRef?: Ref<HTMLDivElement>;
  children: ReactNode;
  className?: string;
}

/**
 * The anchored bubble of the notes (planche 09, hypothesis 10): a home-made
 * panel rendered in a portal, because the grid's container clips its overflow,
 * placed beside the cell it is about and following it while the page scrolls.
 * It closes on Escape and on a click anywhere else; the controls that open
 * notes (`data-note-trigger`) are not « elsewhere », they decide for
 * themselves.
 *
 * Focus: opening puts it on the bubble unless something inside already took it
 * (the composer, for « Ajouter une note »); closing gives it back to the
 * control that opened the bubble, or to the anchor once that control is gone.
 * Not modal, so there is no focus trap.
 */
export function NotesBubble({
  anchor,
  label,
  onClose,
  containerRef,
  children,
  className,
}: NotesBubbleProps) {
  const ownRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<BubblePlacement | null>(null);
  /** Only a bubble taller than the window scrolls: otherwise it must not clip the level menu that opens out of it. */
  const [scrollable, setScrollable] = useState(false);
  /** What had the focus when the bubble was first rendered: the marker or button that opened it. */
  const openerRef = useRef<Element | null | undefined>(undefined);
  if (openerRef.current === undefined) openerRef.current = document.activeElement;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  function setRefs(node: HTMLDivElement | null) {
    ownRef.current = node;
    if (typeof containerRef === 'function') containerRef(node);
    else if (containerRef) (containerRef as { current: HTMLDivElement | null }).current = node;
  }

  // Focus goes in on opening (below) and back to the opener on closing. The
  // cleanup runs before the bubble leaves the page, so it can tell whether the
  // focus is about to be lost with it.
  useLayoutEffect(() => {
    return () => {
      const opener = openerRef.current;
      const active = document.activeElement;
      const lost = active === null || active === document.body || ownRef.current?.contains(active);
      if (!lost) return;
      const target =
        opener instanceof HTMLElement && opener.isConnected && opener !== document.body
          ? opener
          : (anchor.querySelector<HTMLElement>(`[${NOTE_TRIGGER_ATTRIBUTE}]`) ?? anchor);
      target.focus({ preventScroll: true });
    };
  }, [anchor]);

  useEffect(() => {
    const bubble = ownRef.current;
    if (bubble && !bubble.contains(document.activeElement)) bubble.focus({ preventScroll: true });
  }, []);

  // Measured after every render too: the discussions grow the bubble as they load.
  useLayoutEffect(() => {
    function place() {
      const bubble = ownRef.current;
      if (!bubble) return;
      if (!anchor.isConnected) {
        onCloseRef.current();
        return;
      }
      setScrollable(bubble.scrollHeight > window.innerHeight - 2 * GAP_PX);
      const rect = anchor.getBoundingClientRect();
      const next = placeBubble(
        { left: rect.left, right: rect.right, top: rect.top },
        { width: bubble.offsetWidth, height: bubble.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
      );
      setPlacement((current) =>
        current &&
        current.left === next.left &&
        current.top === next.top &&
        current.side === next.side
          ? current
          : next,
      );
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : null;
    if (ownRef.current) observer?.observe(ownRef.current);
    // A zoom or a column resize moves the anchor without any scroll.
    observer?.observe(anchor);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      observer?.disconnect();
    };
  }, [anchor]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onCloseRef.current();
    }
    function handlePointerDown(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (ownRef.current?.contains(target)) return;
      if (target.closest(`[${NOTE_TRIGGER_ATTRIBUTE}]`)) return;
      onCloseRef.current();
    }
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  return createPortal(
    <div
      ref={setRefs}
      // biome-ignore lint/a11y/useSemanticElements: a non-modal popover placed by hand, which the native <dialog> does not offer.
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      data-testid="notes-bubble"
      data-side={placement?.side}
      style={{
        width: NOTES_BUBBLE_WIDTH_PX,
        left: placement?.left ?? 0,
        top: placement?.top ?? 0,
        // Measured once before being shown, so it never flashes at the corner.
        // Transparent rather than `visibility: hidden`, which would refuse the focus.
        opacity: placement ? 1 : 0,
      }}
      className={cn(
        'fixed z-40 rounded-lg border border-gray-200 bg-white p-3 text-noir shadow-lg outline-none',
        scrollable && 'max-h-[calc(100vh-16px)] overflow-y-auto',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
